import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/Login.css';

const LoginPage = () => {
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    // Oturum suresi doldugu icin buraya atildiysa sebebini soyle — kullanici
    // neden cikarildigini bilmeden "panel bozuldu" diye dusunuyordu.
    useEffect(() => {
        let expired = null;
        try {
            expired = sessionStorage.getItem('sessionExpired');
            if (expired) sessionStorage.removeItem('sessionExpired');
        } catch {
            // sessionStorage erisilemezse mesaj gosterilmez
        }
        if (expired) {
            message.info('Oturum süreniz sona erdi, lütfen tekrar giriş yapın.');
        }
    }, []);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            await login(values.username, values.password);
            message.success('Giriş başarılı!');
            navigate('/admin');
        } catch (err) {
            message.error('Kullanıcı adı veya şifre hatalı!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            {/* Background Elements */}
            <div className="login-bg-gradient" />
            <div className="login-bg-pattern" />

            {/* Floating shapes for depth */}
            <motion.div
                className="login-floating-shape shape-1"
                animate={{
                    y: [0, -20, 0],
                    rotate: [0, 5, 0],
                }}
                transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />
            <motion.div
                className="login-floating-shape shape-2"
                animate={{
                    y: [0, 20, 0],
                    rotate: [0, -5, 0],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            {/* Login Card */}
            <motion.div
                className="login-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                {/* Logo Section */}
                <div className="login-header">
                    <motion.div
                        className="login-logo"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    >
                        <span className="login-logo-icon">✈️</span>
                    </motion.div>
                    <motion.h1
                        className="login-title"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        Lidya Travel
                    </motion.h1>
                    <motion.p
                        className="login-subtitle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        Yönetim Paneline Hoş Geldiniz
                    </motion.p>
                </div>

                {/* Form Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <Form
                        onFinish={onFinish}
                        size="large"
                        className="login-form"
                    >
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: 'Kullanıcı adı gerekli' }]}
                        >
                            <Input
                                prefix={<UserOutlined className="login-input-icon" />}
                                placeholder="Kullanıcı Adı"
                                className="login-input"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: 'Şifre gerekli' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined className="login-input-icon" />}
                                placeholder="Şifre"
                                className="login-input"
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={loading}
                                    className="login-button"
                                >
                                    <span>Giriş Yap</span>
                                    {!loading && <ArrowRightOutlined />}
                                </Button>
                            </motion.div>
                        </Form.Item>
                    </Form>
                </motion.div>

                {/* Footer */}
                <motion.div
                    className="login-footer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    <p>© 2024 Lidya Yazılım. Tüm hakları saklıdır.</p>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default LoginPage;
